// packages/api-client/src/services/conversationService.ts
import { EventSourcePolyfill, EventSourcePolyfillInit } from 'event-source-polyfill';
import { getApiClient, getRawApiBaseUrl } from '../client';
import { handleApiError } from '../utils/handleApiError';
import type {
  ConversationSummary,
  ConversationDetail,
  InitTestChatPayload, 
  InitTestChatResponse, 
  StreamRequestBody,
  SSEMessageEventData, 
  ServiceResponse,
  PaginatedResponse,
  UUID,
  ApiSuccessResponse,
  ApiErrorResponse,
  BranchConversationResponse, 
  ConversationVersionSummary,
  RevertConversationResponse
} from '@dopomogai/types';

type PatchOp =
  | { op: 'delete'; index: number }
  | { op: 'insert'; index: number; message?: any; messages?: any[] }
  | { op: 'append'; message?: any; messages?: any[] }
  | { op: 'replace'; index: number; message: any }
  | { op: 'move'; from_index: number; to_index: number }
  | { op: 'delete_part'; message_index: number; part_index: number }
  | { op: 'insert_part'; message_index: number; part_index: number; part: any }
  | { op: 'append_part'; message_index: number; part: any }
  | { op: 'replace_part'; message_index: number; part_index: number; part: any }
  | { op: 'move_part'; message_index: number; from_part_index: number; to_part_index: number };

// NEW: Attachments summary type
export interface ConversationAttachmentsItem {
  message_index: number;
  files: Array<{
    file_id: string;
    filename: string;
    content_type?: string | null;
    logical_path?: string | null;
    tokens_estimated?: number | null;
  }>;
  group_tokens_estimated?: number | null;
}

export const conversationService = {
  /**
   * List Conversations
   * Maps to: GET /api/v1/conversations
   */
  listConversations: (
    agentId: UUID | null = null,
    limit: number = 20,
    offset: number = 0,
    searchQuery: string | null = null,
    sortBy: string = 'updated_at',
    sortOrder: 'asc' | 'desc' = 'desc'
  ): ServiceResponse<PaginatedResponse<ConversationSummary>> => {
    const params = new URLSearchParams();
    if (agentId) params.append('agent_id', agentId);
    params.append('limit', String(limit));
    params.append('offset', String(offset));
    if (searchQuery && searchQuery.trim() !== '') params.append('search_query', searchQuery);
    params.append('sort_by', sortBy);
    params.append('sort_order', sortOrder);

    return getApiClient().get<PaginatedResponse<ConversationSummary>>(`/conversations`, { params })
      .then(response => response.data)
      .catch(err => handleApiError(err, 'listing conversations'));
  },

  /**
   * Get Conversation Detail
   * Maps to: GET /api/v1/conversations/{id}
   * Returns ModelMessages dump (not raw DB message rows).
   */
  getConversationById: (
    conversationId: UUID,
    includeInternalParts: boolean = false
  ): ServiceResponse<ConversationDetail> => {
    if (!conversationId) {
      return Promise.reject(new Error("Conversation ID is required."));
    }
    const params = new URLSearchParams();
    if (includeInternalParts) {
      params.append('include_internal_parts', 'true');
    }
    
    return getApiClient().get<ConversationDetail>(`/conversations/${conversationId}`, { params })
      .then(response => response.data)
      .catch(err => handleApiError(err, `fetching conversation ${conversationId}`));
  },

  /**
   * Get Summary
   * Maps to: GET /api/v1/conversations/{id}/summary
   */
  getConversationSummary: (conversationId: UUID): ServiceResponse<any> => {
    return getApiClient().get(`/conversations/${conversationId}/summary`)
      .then(response => response.data)
      .catch(err => handleApiError(err, `fetching summary for conversation ${conversationId}`));
  },

  generateSummary: (conversationId: UUID): ServiceResponse<import('@dopomogai/types').ConversationSummaryDetail> => {
    return getApiClient()
      .post<import('@dopomogai/types').ConversationSummaryDetail>(`/conversations/${conversationId}/summary/generate`)
      .then(r => r.data)
      .catch(err => handleApiError(err, `generating summary for conversation ${conversationId}`));
  },

  /**
   * Calculate Token Usage
   * Maps to: POST /api/v1/context/usage 
   */
  getContextUsage: (payload: { conversation_id: string; agent_id: string; include_files?: string[] }): ServiceResponse<any> => {
    return getApiClient()
      .post(`/context/usage`, payload)
      .then(res => res.data)
      .catch(err => handleApiError(err, 'calculating context usage'));
  },

  /**
   * Update Conversation Metadata (Rename)
   * Maps to: PATCH /api/v1/conversations/{id}/meta
   */
  updateConversationMeta: (conversationId: UUID, title: string): ServiceResponse<{ id: UUID; title: string }> => {
    return getApiClient()
      .patch(`/conversations/${conversationId}/meta`, { title })
      .then(res => res.data)
      .catch(err => handleApiError(err, 'updating conversation meta'));
  },

  /**
   * DELETE a conversation.
   * Maps to: DELETE /api/v1/conversations/{id}
   */
  deleteConversation: (conversationId: UUID): ServiceResponse<ApiSuccessResponse> => {
    return getApiClient()
      .delete(`/conversations/${conversationId}`)
      .then(() => ({ status: 'success' } as const))
      .catch(err => handleApiError(err, `deleting conversation ${conversationId}`));
  },

  /**
   * BRANCH (Edit) a conversation.
   * Maps to: POST /api/v1/conversations/{id}/branch
   */
  branchConversation: (conversationId: UUID, messageIndex: number): ServiceResponse<BranchConversationResponse> => {
    return getApiClient()
      .post(`/conversations/${conversationId}/branch`, { message_index: messageIndex })
      .then(res => res.data)
      .catch(err => handleApiError(err, 'branching conversation'));
  },

  /**
   * PATCH messages (manual edit)
   * Maps to: PATCH /api/v1/conversations/{id}/messages
   */
  patchConversationMessages: (
    conversationId: UUID,
    operations: PatchOp[]
  ): ServiceResponse<{ conversation_id: UUID; messages: any[]; message_count: number }> => {
    return getApiClient()
      .patch(`/conversations/${conversationId}/messages`, { operations })
      .then(res => res.data)
      .catch(err => handleApiError(err, `patching messages for ${conversationId}`));
  },

  /**
   * Get Version History (Time Travel)
   * Maps to: GET /api/v1/conversations/{id}/versions
   */
  getConversationVersions: (conversationId: string): ServiceResponse<ConversationVersionSummary[]> => {
    return getApiClient()
      .get<ConversationVersionSummary[]>(`/conversations/${conversationId}/versions`)
      .then(res => res.data)
      .catch(err => handleApiError(err, 'fetching conversation versions'));
  },

  /**
   * Revert Conversation (Time Travel)
   * Maps to: POST /api/v1/conversations/{id}/revert
   */
  revertConversation: (conversationId: string, targetVersionId: string): ServiceResponse<RevertConversationResponse> => {
    return getApiClient()
      .post<RevertConversationResponse>(`/conversations/${conversationId}/revert`, {
        target_version_id: targetVersionId
      })
      .then(res => res.data)
      .catch(err => handleApiError(err, 'reverting conversation'));
  },

  /**
   * Fork from specific Version
   * Maps to: POST /api/v1/conversations/{id}/branch_version
   */
  branchFromVersion: (conversationId: string, sourceVersionId: string, newAgentId?: string): ServiceResponse<BranchConversationResponse> => {
    return getApiClient()
      .post<BranchConversationResponse>(`/conversations/${conversationId}/branch_version`, {
        source_version_id: sourceVersionId,
        new_agent_id: newAgentId
      })
      .then(res => res.data)
      .catch(err => handleApiError(err, 'branching from version'));
  },

  /**
   * Initializes a secure test chat session.
   */
  initTestChatSession: (
    agentId: UUID,
    initialContext?: InitTestChatPayload['initial_context'] 
  ): ServiceResponse<InitTestChatResponse> => {
    if (!agentId) {
      const error = { isError: true, message: 'Agent ID is required.', status: 400, originalError: new Error("Agent ID required") } as ApiErrorResponse;
      return Promise.resolve(error);
    }
    const payload: InitTestChatPayload = initialContext ? { initial_context: initialContext } : {};
    return getApiClient().post<InitTestChatResponse>(`/agents/${agentId}/init-test-chat`, payload)
      .then(response => response.data)
      .catch(error => handleApiError(error, `initialize test chat session for agent ${agentId}`));
  },

  /**
   * Stream a conversation via Server-Sent Events (SSE).
   */
  streamConversation: (
    agentId: UUID,
    body: StreamRequestBody,
    token: string, 
    onMessage: (event: MessageEvent<any>, parsedData: SSEMessageEventData | null) => void, 
    onError: (errorEvent: Event | any) => void, 
    onOpen?: (event: Event) => void
  ): EventSourcePolyfill | null => { 
    if (!agentId || !body || !token || !onMessage || !onError) {
      const errorMsg = "streamConversation: Missing required parameters.";
      console.error(errorMsg);
      onError({ type: 'error', message: errorMsg, isConfigurationError: true });
      return null;
    }

    const rootApiUrl = getRawApiBaseUrl(); 
    if (!rootApiUrl) {
        const errorMsg = "streamConversation: API base URL not configured.";
        console.error(errorMsg);
        onError({ type: 'error', message: errorMsg, isConfigurationError: true });
        return null;
    }
    const url = `${rootApiUrl}/api/v1/agents/${agentId}/conversations/stream`;

    const eventSourceInitDict: EventSourcePolyfillInit = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    };

    try {
      const es = new EventSourcePolyfill(url, eventSourceInitDict);

      if (onOpen) es.onopen = onOpen;

      es.onmessage = (event: MessageEvent<any>) => {
        let parsedData: SSEMessageEventData | null = null;
        try {
          if (event.data && typeof event.data === 'string') {
            parsedData = JSON.parse(event.data) as SSEMessageEventData;
          }
        } catch (e) {
          console.warn("SSE: Failed to parse message data:", event.data, e);
        }
        onMessage(event, parsedData);
      };

      es.onerror = (errorEvent: Event | any) => {
        console.error("SSE Connection Error:", errorEvent);
        onError(errorEvent);
      };

      return es;
    } catch (setupError: any) {
        console.error("SSE Setup Error:", setupError);
        onError({ type: 'error', message: setupError.message || "Failed to initialize SSE connection.", originalError: setupError });
        return null;
    }
  },

  /**
   * NEW: Per-message attachments summary (group tokens)
   * GET /api/v1/conversations/{id}/attachments
   */
  getAttachments: (conversationId: string): ServiceResponse<ConversationAttachmentsItem[]> => {
    return getApiClient()
      .get<ConversationAttachmentsItem[]>(`/conversations/${conversationId}/attachments`)
      .then(res => res.data)
      .catch(err => handleApiError(err, 'fetching conversation attachments'));
  },
};